import { useCallback } from 'react';
import { EAttributeType } from '../../../@types/AttributeTypes';
import { IBuildingProviderContext, ITexture } from '../../../@types/ContextProviderRelated';
import { EHistoryAction } from '../../../hooks/useUndoRedo';
import AttributeModel from '../../../models/standard/AttributeModel';
import FloorModel from '../../../models/standard/FloorModel';
import RoomModel from '../../../models/standard/RoomModel';
import SpaceModel from '../../../models/standard/SpaceModel';
import { DefaultAttributeId } from '../../../utils/attributes/defaultObjectsAndAttributes';

// Updated Interface: All functions now require floorUUID
export interface IFloorActions {
  setFloorTexture: (floorUUID: string, texture: ITexture | null) => void;
  resetFloor: (floorUUID: string) => void;
  updateFloorAttributes: (floorUUID: string, attributes: AttributeModel[]) => void;
  setFloorGeometryAttributes: (floorUUID: string, area: number, areaUnit: string | null, perimeter: number, perimeterUnit: string | null) => void;
}

export const useFloorActions = (setBuilding: React.Dispatch<React.SetStateAction<IBuildingProviderContext>>): IFloorActions => {
  /**
   * Helper: Traverses Rooms -> Spaces -> Floor (1:1) to find the specific UUID
   * and applies the updater function to that floor.
   * This preserves all Class Instances (RoomModel, SpaceModel).
   */
  const updateFloorInBuilding = (prev: IBuildingProviderContext, floorUUID: string, updater: (oldFloor: FloorModel) => FloorModel): IBuildingProviderContext => {
    if (!prev || !prev.rooms) return prev;

    let hasChanges = false;

    const newRooms = prev.rooms.map((room) => {
      // Optimization: If room has no spaces, skip
      if (!room.spaces) return room;

      let roomChanged = false;

      const newSpaces = room.spaces.map((space) => {
        // Access single floor (1:1)
        const currentFloor = (space as any).floor as FloorModel | undefined;

        // If no floor exists or UUID mismatch, return original space
        if (!currentFloor || currentFloor.uuid !== floorUUID) {
          return space;
        }

        // Found the floor!
        hasChanges = true;
        roomChanged = true;

        // Run the updater logic
        const nextFloor = updater(currentFloor);

        // Ensure UUID is preserved if the updater didn't set it explicitly
        if (currentFloor.uuid && !nextFloor.uuid) {
          nextFloor.setUUID(currentFloor.uuid);
        }

        // Re-instantiate SpaceModel to preserve methods with SINGLE floor argument
        const newSpace = new SpaceModel(
          nextFloor,
          space.products,
          space.elements,
          space.models2D,
          space.boundaries,
          space.walls,
        );
        if (space.uuid) newSpace.setUUID(space.uuid);

        return newSpace;
      });

      if (!roomChanged) return room;

      // Re-instantiate RoomModel to preserve methods
      const newRoom = new RoomModel(room.title, newSpaces, room.attributes?.map((a) => ({ ...a })) ?? [], room.uuid);

      return newRoom;
    });

    // If we didn't find the floor anywhere, return original state
    if (!hasChanges) return prev;

    return {
      ...prev,
      rooms: newRooms,
    };
  };

  const setFloorTexture = useCallback(
    (floorUUID: string, texture: ITexture | null): void => {
      setBuilding((prev) => {
        const next = updateFloorInBuilding(prev, floorUUID, (oldFloor) => {
          return new FloorModel(
            oldFloor.tiles,
            texture ? { ...texture } : null, // Deep copy texture if exists
            oldFloor.attributes?.map((a) => ({ ...a })) ?? [],
            oldFloor.uuid,
          );
        });

        return {
          ...next,
          isSetByUndoRedo: false,
          lastAction: EHistoryAction.FLOOR_TEXTURE_CHANGED,
        };
      });
    },
    [setBuilding],
  );

  const resetFloor = useCallback(
    (floorUUID: string): void => {
      setBuilding((prev) => {
        const next = updateFloorInBuilding(prev, floorUUID, (oldFloor) => {
          // Resetting tiles and texture, keeping attributes and UUID
          return new FloorModel(
            undefined,
            undefined,
            oldFloor?.attributes?.map((a) => ({ ...a })),
            oldFloor?.uuid,
          );
        });

        return {
          ...next,
          isSetByUndoRedo: false,
          lastAction: EHistoryAction.FLOOR_RESET,
        };
      });
    },
    [setBuilding],
  );

  const updateFloorAttributes = useCallback(
    (floorUUID: string, attributes: AttributeModel[]): void => {
      setBuilding((prev) => {
        const next = updateFloorInBuilding(prev, floorUUID, (oldFloor) => {
          return new FloorModel(
            oldFloor.tiles,
            oldFloor.texture,
            attributes, // Replace attributes
            oldFloor.uuid,
          );
        });

        return {
          ...next,
          isSetByUndoRedo: false,
          lastAction: EHistoryAction.FLOOR_ATTRIBUTES_UPDATED,
        };
      });
    },
    [setBuilding],
  );

  const setFloorGeometryAttributes = useCallback(
    (floorUUID: string, area: number, areaUnit: string | null, perimeter: number, perimeterUnit: string | null): void => {
      setBuilding((prev) => {
        const next = updateFloorInBuilding(prev, floorUUID, (oldFloor) => {
          const attrs = oldFloor?.attributes ?? [];

          // Map over existing attributes to update Area and Perimeter
          const updatedAttrs = attrs.map((a) => {
            if (a.id === DefaultAttributeId.FLOOR_AREA) {
              return new AttributeModel(
                a.id,
                a.key,
                area,
                a.predecessorId,
                EAttributeType.NUMBER,
                areaUnit,
                a.hidden,
                a.forced,
                a.ownerId,
                a.userId,
                a.publicFlag,
                a.localization,
                a.defaults,
                a.validation,
                a.unitFamily,
              );
            }
            if (a.id === DefaultAttributeId.FLOOR_PERIMETER) {
              return new AttributeModel(
                a.id,
                a.key,
                perimeter,
                a.predecessorId,
                EAttributeType.NUMBER,
                perimeterUnit,
                a.hidden,
                a.forced,
                a.ownerId,
                a.userId,
                a.publicFlag,
                a.localization,
                a.defaults,
                a.validation,
                a.unitFamily,
              );
            }
            return a;
          });

          return new FloorModel(oldFloor.tiles, oldFloor.texture, updatedAttrs, oldFloor.uuid);
        });

        return {
          ...next,
          isSetByUndoRedo: false,
          lastAction: EHistoryAction.FLOOR_GEOMETRY_ATTRIBUTES_UPDATED,
        };
      });
    },
    [setBuilding],
  );

  return {
    setFloorTexture,
    resetFloor,
    updateFloorAttributes,
    setFloorGeometryAttributes,
  };
};